import API_BASE_URL from 'src/constants';
import { handleUnauthorized } from './authToken';

export type AiChatRole = 'user' | 'assistant';

/** A file the user uploaded via `POST /portal/ai_chat/upload` and attached to a message. */
export interface AiChatAttachment {
  attachmentId: number;
  accessToken: string;
  filename: string;
  mimetype: string;
  size: number;
  pages?: number | null;
}

export interface AiChatMessage {
  role: AiChatRole;
  content: string;
  attachments?: AiChatAttachment[];
}

/** An exported file the assistant produced and offers for download. */
export interface AiChatFile {
  attachmentId: number;
  accessToken: string;
  filename: string;
  mimetype: string;
}

export type AiChatChartType = 'bar' | 'horizontal_bar' | 'stacked_bar' | 'line' | 'area' | 'pie' | 'doughnut';

export interface AiChatChartDataset {
  label: string;
  data: (number | null)[];
}

/** A chart spec the assistant rendered via its `render_chart` tool (normalised by the backend). */
export interface AiChatChart {
  type: AiChatChartType;
  title: string;
  labels: string[];
  datasets: AiChatChartDataset[];
  x_label: string;
  y_label: string;
  value_format: 'number' | 'currency' | 'percent';
  currency: string;
}

export interface AiChatCallbacks {
  onText: (delta: string) => void;
  onToolUse?: (name: string) => void;
  onFile?: (file: AiChatFile) => void;
  onChart?: (chart: AiChatChart) => void;
  onError?: (message: string) => void;
  onDone?: () => void;
}

interface AiChatFrame {
  type: 'text' | 'tool_use' | 'file' | 'chart' | 'error' | 'done';
  text?: string;
  name?: string;
  message?: string;
  chart?: AiChatChart;
  // file frame (snake_case from the backend)
  attachment_id?: number;
  access_token?: string;
  filename?: string;
  mimetype?: string;
}

export const AI_CHAT_ACCEPT = '.pdf,.png,.jpg,.jpeg,.gif,.webp,.csv,.txt,.xlsx';
export const AI_CHAT_MAX_UPLOAD_BYTES = 20 * 1024 * 1024;

/**
 * Stream an AI chat reply from the in-house backend (`POST /portal/ai_chat`),
 * which emits Server-Sent-Events frames. Resolves once the stream completes.
 * Pass an AbortSignal to cancel an in-flight request.
 */
export async function streamAiChat(
  messages: AiChatMessage[],
  cb: AiChatCallbacks,
  signal?: AbortSignal,
): Promise<void> {
  const accessToken = localStorage.getItem('access_token');

  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}portal/ai_chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      // The backend is stateless and only accepts role/content/attachment refs;
      // strip any client-side extras (rendered files, charts) from the history.
      body: JSON.stringify({
        messages: messages.map(({ role, content, attachments }) => ({
          role,
          content,
          attachments: (attachments ?? []).map(a => ({
            attachment_id: a.attachmentId,
            access_token: a.accessToken,
          })),
        })),
      }),
      signal,
    });
  } catch (err) {
    if ((err as Error)?.name !== 'AbortError') {
      cb.onError?.((err as Error)?.message ?? 'Network error');
    }
    cb.onDone?.();
    return;
  }

  if (!response.ok || !response.body) {
    if (response.status === 401) handleUnauthorized(accessToken);
    cb.onError?.(`Request failed (${response.status})`);
    cb.onDone?.();
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  const handleFrame = (raw: string) => {
    const dataLine = raw.split('\n').find(line => line.startsWith('data:'));
    if (!dataLine) return;
    const json = dataLine.slice(5).trim();
    if (!json) return;
    let frame: AiChatFrame;
    try {
      frame = JSON.parse(json) as AiChatFrame;
    } catch {
      return;
    }
    switch (frame.type) {
      case 'text':
        if (frame.text) cb.onText(frame.text);
        break;
      case 'tool_use':
        if (frame.name) cb.onToolUse?.(frame.name);
        break;
      case 'file':
        if (frame.attachment_id != null && frame.access_token && frame.filename) {
          cb.onFile?.({
            attachmentId: frame.attachment_id,
            accessToken: frame.access_token,
            filename: frame.filename,
            mimetype: frame.mimetype ?? 'application/octet-stream',
          });
        }
        break;
      case 'chart':
        if (frame.chart && Array.isArray(frame.chart.labels) && Array.isArray(frame.chart.datasets)) {
          cb.onChart?.(frame.chart);
        }
        break;
      case 'error':
        cb.onError?.(frame.message ?? 'Unknown error');
        break;
      default:
        break;
    }
  };

  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      // eslint-disable-next-line no-await-in-loop
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let sep = buffer.indexOf('\n\n');
      while (sep !== -1) {
        handleFrame(buffer.slice(0, sep));
        buffer = buffer.slice(sep + 2);
        sep = buffer.indexOf('\n\n');
      }
    }
    if (buffer.trim()) handleFrame(buffer);
  } catch (err) {
    if ((err as Error)?.name !== 'AbortError') {
      cb.onError?.((err as Error)?.message ?? 'Stream error');
    }
  } finally {
    cb.onDone?.();
  }
}

/**
 * Upload a file to attach to the next chat message. Resolves with the
 * attachment coordinates the backend expects in `messages[].attachments`.
 */
export async function uploadAiChatFile(file: File, signal?: AbortSignal): Promise<AiChatAttachment> {
  const accessToken = localStorage.getItem('access_token');
  const form = new FormData();
  form.append('file', file, file.name);

  const response = await fetch(`${API_BASE_URL}portal/ai_chat/upload`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}` },
    body: form,
    signal,
  });
  if (!response.ok) {
    if (response.status === 401) handleUnauthorized(accessToken);
    let detail = `Upload failed (${response.status})`;
    try {
      const body = (await response.json()) as { detail?: string };
      if (typeof body?.detail === 'string') detail = body.detail;
    } catch {
      // keep the generic message
    }
    throw new Error(detail);
  }
  const body = (await response.json()) as {
    attachment_id: number;
    access_token: string;
    filename: string;
    mimetype: string;
    size: number;
    pages?: number | null;
  };
  return {
    attachmentId: body.attachment_id,
    accessToken: body.access_token,
    filename: body.filename,
    mimetype: body.mimetype,
    size: body.size,
    pages: body.pages ?? null,
  };
}

export function saveBlob(blob: Blob, filename: string): void {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(objectUrl);
}

/**
 * Download a file the assistant exported. The download endpoint needs the same
 * JWT as the chat, so we fetch it with the Authorization header and hand the
 * browser a blob (a plain <a href> can't carry the bearer token).
 */
export async function downloadAiChatFile(file: AiChatFile): Promise<void> {
  const accessToken = localStorage.getItem('access_token');
  const url =
    `${API_BASE_URL}portal/ai_chat/download` +
    `?attachment_id=${file.attachmentId}` +
    `&access_token=${encodeURIComponent(file.accessToken)}`;

  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!response.ok) {
    if (response.status === 401) handleUnauthorized(accessToken);
    throw new Error(`Download failed (${response.status})`);
  }

  const blob = await response.blob();
  saveBlob(blob, file.filename);
}
